/**
 * Attorney Questions form rules (mig 1610, tester requests 2026-11-03). Pure, so they are unit-tested.
 * The server (create_attorney_question) enforces the same rules; these only decide what the form shows.
 */

/** contact_member_category(): current / former / never, or unknown for a missing contact. */
export type MemberCategoryValue = "current" | "former" | "never" | "unknown";

/** Only CURRENT members may have an attorney question raised (owner 2026-11-03; reverses 2026-09-20). */
export function isCurrentMember(category: string | null | undefined): boolean {
  return category === "current";
}

export const MEMBER_STATUS_LABEL: Record<MemberCategoryValue, string> = {
  current: "Current member",
  former: "Former member",
  never: "Not a member",
  unknown: "Not a member",
};

export function memberStatusLabel(category: string | null | undefined): string {
  return (
    MEMBER_STATUS_LABEL[(category ?? "unknown") as MemberCategoryValue] ??
    MEMBER_STATUS_LABEL.unknown
  );
}

/** Plain-language reason a contact cannot be the subject, or null when they can. */
export function memberRefusalReason(
  category: string | null | undefined,
): string | null {
  if (isCurrentMember(category)) {
    return null;
  }
  if (category === "former") {
    return "This person is a former member: their membership has ended. Attorney questions can only be raised for current members.";
  }
  return "This person is not a member. Attorney questions can only be raised for current members.";
}

/**
 * Preview reasons that mean "the member's ZIP cannot place them in a region of a split state" — the case where the
 * agent chooses the region (Texas north/south). Every other unroutable reason stays "assign by hand".
 */
export const AGENT_REGION_PICK_REASONS: readonly string[] = [
  "needs_texas_zip",
  "needs_member_zip_in_state",
];

export function needsAgentRegionPick(
  reason: string | null | undefined,
): boolean {
  return !!reason && AGENT_REGION_PICK_REASONS.includes(reason);
}

/**
 * The state to pre-select for an Attorney Required question: the member's own state from their record, when it is
 * one an attorney can be chosen for. Otherwise null (the agent picks).
 */
export function defaultOwnState(
  memberState: string | null | undefined,
  selectableStates: readonly string[],
): string | null {
  const s = (memberState ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(s)) {
    return null;
  }
  return selectableStates.includes(s) ? s : null;
}
